import * as React from "react";
import { AiOutlineBtn } from "./components/ui/AiOutlineBtn";
import { AiSpinner } from "./components/ui/AiSpinner";
import { IconRefresh } from "./icons";
import { InlineConfirmBar } from "./InlineConfirmBar";
import type { ConfirmAction } from "./types";

export type StatusHeaderProps = {
  reviewsNeededLabel: string | null;
  needsAttentionCount: number;
  needsReviewCount: number;
  compliantCount: number;
  totalRows: number;
  hasResults: boolean;
  loading: boolean;
  /** True while a background analysis job is in progress (Re-run → Running…). */
  running?: boolean;
  showError: boolean;
  errorMessage?: string;
  onRetryRun?: () => void;
  onRerun?: () => void;
  pendingCount: number;
  confirmAction: ConfirmAction;
  bulkBusy: boolean;
  acceptAllRunning: boolean;
  rejectAllRunning: boolean;
  onAcceptAllClick: () => void;
  onRejectAllClick: () => void;
  onConfirmAcceptAll: () => void;
  onConfirmRejectAll: () => void;
  onCancelConfirm: () => void;
};

export const StatusHeader: React.FC<StatusHeaderProps> = ({
  reviewsNeededLabel,
  needsAttentionCount,
  needsReviewCount,
  compliantCount,
  totalRows,
  hasResults,
  loading,
  running = false,
  showError,
  errorMessage,
  onRetryRun,
  onRerun,
  pendingCount,
  confirmAction,
  bulkBusy,
  acceptAllRunning,
  rejectAllRunning,
  onAcceptAllClick,
  onRejectAllClick,
  onConfirmAcceptAll,
  onConfirmRejectAll,
  onCancelConfirm,
}) => {
  if (showError) {
    return (
      <div className="ai-analysis__status-header ai-analysis__status-header--error">
        <p className="ai-analysis__status-title">Couldn&apos;t analyse this document</p>
        {errorMessage ? <p className="ai-analysis__meta" style={{ margin: "6px 0 0", color: "var(--bad)" }}>{errorMessage}</p> : null}
        {onRetryRun ? (
          <div style={{ marginTop: 10 }}>
            <AiOutlineBtn onClick={onRetryRun}>Try again</AiOutlineBtn>
          </div>
        ) : null}
      </div>
    );
  }

  if (loading && !hasResults) {
    return (
      <div className="ai-analysis__status-header" aria-busy="true">
        <div className="ai-analysis__status-row">
          <p className="ai-analysis__status-title">AI analysis</p>
        </div>
        {running ? (
          <div className="ai-analysis__actions-row">
            <AiOutlineBtn
              className="ai-analysis__rerun"
              disabled
              aria-label="Running AI analysis"
            >
              <AiSpinner size="sm" label="Running" />
              Running…
            </AiOutlineBtn>
          </div>
        ) : (
          <>
            <div className="ai-analysis__skeleton-bar" />
            <div className="ai-analysis__legend">
              <div className="ai-analysis__skeleton-pill" />
              <div className="ai-analysis__skeleton-pill" />
              <div className="ai-analysis__skeleton-pill" />
            </div>
            <div className="ai-analysis__actions-row">
              <div className="ai-analysis__skeleton-btn" />
              <div className="ai-analysis__skeleton-btn" />
            </div>
          </>
        )}
      </div>
    );
  }

  const total = Math.max(totalRows, 1);

  return (
    <div className="ai-analysis__status-header">
      <div className="ai-analysis__status-row">
        <p className="ai-analysis__status-title">AI analysis</p>
        {reviewsNeededLabel ? (
          <span className="ai-analysis__meta" aria-live="polite">
            {reviewsNeededLabel}
          </span>
        ) : null}
      </div>

      {hasResults ? (
        <div className="ai-analysis__progress-bar" role="presentation">
          {needsAttentionCount > 0 ? (
            <div
              className="ai-analysis__progress-segment ai-analysis__progress-segment--attention"
              style={{ flex: needsAttentionCount / total }}
            />
          ) : null}
          {needsReviewCount > 0 ? (
            <div
              className="ai-analysis__progress-segment ai-analysis__progress-segment--review"
              style={{ flex: needsReviewCount / total }}
            />
          ) : null}
          {compliantCount > 0 ? (
            <div
              className="ai-analysis__progress-segment ai-analysis__progress-segment--compliant"
              style={{ flex: compliantCount / total }}
            />
          ) : null}
        </div>
      ) : null}

      {hasResults ? (
        <div className="ai-analysis__legend">
          <span className="ai-analysis__legend-item">
            <span className="ai-analysis__legend-swatch" style={{ background: "var(--sev-attention)" }} />
            Attention {needsAttentionCount}
          </span>
          <span className="ai-analysis__legend-item">
            <span className="ai-analysis__legend-swatch" style={{ background: "var(--sev-review)" }} />
            Review {needsReviewCount}
          </span>
          <span className="ai-analysis__legend-item">
            <span className="ai-analysis__legend-swatch" style={{ background: "var(--sev-compliant)" }} />
            Compliant {compliantCount}
          </span>
        </div>
      ) : null}

      {hasResults ? (
        confirmAction ? (
          <InlineConfirmBar
            action={confirmAction}
            count={pendingCount}
            onConfirm={confirmAction === "accept" ? onConfirmAcceptAll : onConfirmRejectAll}
            onCancel={onCancelConfirm}
          />
        ) : (
          <div className="ai-analysis__actions-row">
            {pendingCount > 0 && !loading ? (
              <>
                <AiOutlineBtn
                  disabled={bulkBusy || rejectAllRunning}
                  onClick={onAcceptAllClick}
                >
                  {acceptAllRunning || bulkBusy ? (
                    <>
                      <AiSpinner size="sm" label="Accepting" />
                      Accepting…
                    </>
                  ) : (
                    "Accept all"
                  )}
                </AiOutlineBtn>
                <AiOutlineBtn
                  disabled={bulkBusy || acceptAllRunning}
                  onClick={onRejectAllClick}
                >
                  {rejectAllRunning ? (
                    <>
                      <AiSpinner size="sm" label="Rejecting" />
                      Rejecting…
                    </>
                  ) : (
                    "Reject all"
                  )}
                </AiOutlineBtn>
              </>
            ) : null}
            {onRerun ? (
              <AiOutlineBtn
                className="ai-analysis__rerun"
                disabled={loading || bulkBusy || running}
                onClick={onRerun}
                aria-label={running ? "Running AI analysis" : "Re-run AI analysis"}
              >
                {running ? (
                  <>
                    <AiSpinner size="sm" label="Running" />
                    Running…
                  </>
                ) : (
                  <>
                    <IconRefresh size={11} />
                    Re-run
                  </>
                )}
              </AiOutlineBtn>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
};
